import { useCallback, useEffect, useRef } from 'react';
import { useAudioPlayer } from '../context/AudioPlayerContext';

/**
 * Auto-plays a song via the global audio player when the card is visible.
 * @param {React.RefObject} cardRef
 * @param {Object} song
 * @returns {{ isPlaying: boolean, play: () => Promise<void>, pause: () => void }}
 */
export default function useSongAutoPlay(cardRef, song) {
    const observerRef = useRef(null);
    const { currentTrack, isPlaying, playTrack, pause } = useAudioPlayer();

    const isCurrentSong = Boolean(currentTrack && song && currentTrack.id === song.id);
    const isSongPlaying = isCurrentSong && isPlaying;

    const handlePlay = useCallback(async () => {
        if (!song?.songUrl) return;
        await playTrack({
            id: song.id,
            url: song.songUrl,
            title: song.title || song.name,
            artist: song.artist,
            artwork: song.coverUrl || song.thumbnailUrl || song.imageUrl,
        });
    }, [playTrack, song]);

    const handlePause = useCallback(() => {
        if (isCurrentSong) {
            pause();
        }
    }, [isCurrentSong, pause]);

    useEffect(() => {
        if (!cardRef?.current || !song?.songUrl) return;

        const cardElement = cardRef.current;
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const mostlyVisible = entry.isIntersecting && entry.intersectionRatio >= 0.7;
                    const mostlyHidden = entry.intersectionRatio <= 0.35;

                    if (mostlyVisible) {
                        handlePlay().catch((error) => {
                            // Silently handle autoplay policy errors (expected browser behavior)
                            // Only log unexpected errors
                            if (error.name !== 'NotAllowedError') {
                                console.error('Auto-play failed', error);
                            }
                        });
                    } else if (mostlyHidden && isCurrentSong) {
                        pause();
                    }
                });
            },
            { threshold: [0.35, 0.7], rootMargin: '0px' }
        );

        observerRef.current.observe(cardElement);

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [cardRef, handlePlay, isCurrentSong, pause, song?.songUrl]);

    return {
        isPlaying: isSongPlaying,
        play: handlePlay,
        pause: handlePause,
    };
}
